/**
 * Single SignalR entry point: one hub per company, with the handled events
 * chosen by the current route.
 */
import { DestroyRef, effect, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import type { HubConnection } from '@microsoft/signalr';
import { HubConnectionState } from '@microsoft/signalr';
import { QueryClient } from '@tanstack/angular-query-experimental';
import { MessageService } from 'primeng/api';
import { filter, map, startWith } from 'rxjs';
import {
  ExportNotificationPayloadExportsInnerStatusEnum,
  ExportNotificationPayloadOperationEnum,
  ImportNotificationPayloadImportsInnerStatusEnum,
  ProductNotificationPayloadOperationEnum,
  type BulkUpdateProductActivityNotificationPayload,
  type CompanyHubSubscribe200Response,
  type ExportNotificationPayload,
  type ImportNotificationPayload,
  type ProductNotificationPayload,
  type SyncNotificationPayload,
} from '../connectors/realtime';
import {
  isActivitiesProductUpdateUrl,
  isActivitiesSyncUrl,
} from '../app-route-paths';
import { importDialogOpen } from '../components/organisms/dialogs/import-products-dialog/import-products-dialog.state';
import { COMPANY } from '../providers/company.provider';
import { createSignalrHub, waitForFirstNotification } from './signalr';
import { invalidateDigitalAssetCaches } from './digital-assets.service';
import {
  invalidateProductCaches,
  invalidateProductCountCaches,
  invalidateProductListCaches,
} from './products-service';
import {
  aiFieldContentGenerationsQueryKeys,
  aiGenerationsQueryKeys,
  aiTextTranslationsQueryKeys,
  bulkUpdateProductActivitiesQueryKeys,
  channelsQueryKeys,
  exportsQueryKeys,
  importsQueryKeys,
  productChannelContentQueryKeys,
  productChannelsQueryKeys,
  productsQueryKeys,
  synchronizationQueryKeys,
} from './query-keys';

export type CompanyHubEventName = CompanyHubSubscribe200Response['eventName'];

type CompanyHubCacheInvalidator = (
  queryClient: QueryClient,
  messageService: MessageService,
  payload: unknown,
  /** `Router.url` when the event arrived, for toasts the open page already covers. */
  url: string,
) => void | Promise<void>;

const notifiedImportIds = new Set<string>();

const companyHubCacheInvalidators: Readonly<
  Record<CompanyHubEventName, CompanyHubCacheInvalidator>
> = {
  // Saves update the open detail from their response, so only lists and duplicate
  // lookups refresh here; count badges refresh only on create/delete.
  ProductNotification: (queryClient, _messageService, payload) => {
    void invalidateProductListCaches(queryClient);
    void queryClient.invalidateQueries({
      queryKey: productsQueryKeys.duplicateValues(),
    });
    const { operation } = payload as ProductNotificationPayload;
    if (
      operation === ProductNotificationPayloadOperationEnum.Creation ||
      operation === ProductNotificationPayloadOperationEnum.Deletion
    ) {
      void invalidateProductCountCaches(queryClient);
    }
  },
  // Covers create, update, and deletion (backend Operation.Deletion).
  DigitalAssetNotification: (queryClient) =>
    invalidateDigitalAssetCaches(queryClient),
  DigitalAssetTransformationNotification: (queryClient) =>
    invalidateDigitalAssetCaches(queryClient),
  AiGenerationNotification: async (queryClient) => {
    await invalidateDigitalAssetCaches(queryClient);
    await queryClient.invalidateQueries({
      queryKey: aiGenerationsQueryKeys.all(),
    });
  },
  AiFieldContentGenerationNotification: async (queryClient) => {
    await invalidateProductCaches(queryClient);
    await queryClient.invalidateQueries({
      queryKey: aiFieldContentGenerationsQueryKeys.all(),
    });
  },
  AiFieldTranslationNotification: (queryClient) =>
    invalidateProductCaches(queryClient),
  AiTextTranslationNotification: (queryClient) =>
    queryClient.invalidateQueries({
      queryKey: aiTextTranslationsQueryKeys.all(),
    }),
  SyncNotification: (queryClient, messageService, rawPayload, url) => {
    void queryClient.invalidateQueries({
      queryKey: synchronizationQueryKeys.all(),
    });
    void queryClient.invalidateQueries({
      queryKey: channelsQueryKeys.all(),
    });
    void queryClient.invalidateQueries({
      queryKey: productChannelsQueryKeys.all(),
    });
    void queryClient.invalidateQueries({
      queryKey: productChannelContentQueryKeys.all(),
    });
    void invalidateProductListCaches(queryClient);
    const payload = rawPayload as SyncNotificationPayload;
    for (const sync of payload?.syncs ?? []) {
      // The drawer of that run shows its outcome inline: no toast on top of it.
      if (isActivitiesSyncUrl(url, sync.id)) continue;
      if (sync.status === 'completed') {
        messageService.add({
          severity: 'success',
          summary: $localize`Fatto`,
          detail: $localize`Sincronizzazione completata.`,
          data: { syncId: sync.id },
        });
      } else if (sync.status === 'failed') {
        messageService.add({
          severity: 'error',
          summary: $localize`Errore`,
          detail: $localize`Sincronizzazione fallita.`,
          data: { syncId: sync.id },
        });
      } else if (sync.status === 'partialFailure') {
        messageService.add({
          severity: 'warn',
          summary: $localize`Attenzione`,
          detail: $localize`Sincronizzazione completata con errori.`,
          data: { syncId: sync.id },
        });
      } else if (sync.status === 'halted') {
        messageService.add({
          severity: 'warn',
          summary: $localize`Attenzione`,
          detail: $localize`Sincronizzazione interrotta.`,
          data: { syncId: sync.id },
        });
      }
    }
  },
  BulkUpdateProductActivityNotification: (
    queryClient,
    messageService,
    rawPayload,
    url,
  ) => {
    const payload = rawPayload as BulkUpdateProductActivityNotificationPayload;

    for (const scope of ['list', 'get', 'records'] as const) {
      void queryClient.invalidateQueries({
        queryKey: [...bulkUpdateProductActivitiesQueryKeys.all(), scope],
      });
    }
    // Each chunk names the products it wrote; creation, stop and end name none and touch them all.
    const productIds = payload?.productIds ?? [];
    if (productIds.length > 0) {
      for (const productId of productIds) {
        void queryClient.invalidateQueries({
          queryKey:
            bulkUpdateProductActivitiesQueryKeys.pendingForProduct(productId),
        });
      }
    } else {
      void queryClient.invalidateQueries({
        queryKey: [...bulkUpdateProductActivitiesQueryKeys.all(), 'pending'],
      });
    }

    // The products are refreshed once at the end rather than on every chunk the run writes.
    const ended = (payload?.activities ?? []).some((activity) =>
      ['completed', 'partialFailure', 'failed', 'halted'].includes(
        activity.status,
      ),
    );
    if (ended) {
      void invalidateProductCaches(queryClient);
    }

    for (const activity of payload?.activities ?? []) {
      // The drawer of that update shows its outcome inline: no toast on top of it.
      if (isActivitiesProductUpdateUrl(url, activity.id)) continue;
      if (activity.status === 'completed') {
        messageService.add({
          severity: 'success',
          summary: $localize`Fatto`,
          detail: $localize`Aggiornamento prodotti completato.`,
          data: { productUpdateId: activity.id },
        });
      } else if (activity.status === 'partialFailure') {
        messageService.add({
          severity: 'warn',
          summary: $localize`Attenzione`,
          detail: $localize`Aggiornamento prodotti completato con errori.`,
          data: { productUpdateId: activity.id },
        });
      } else if (activity.status === 'failed') {
        messageService.add({
          severity: 'error',
          summary: $localize`Errore`,
          detail: $localize`Aggiornamento prodotti fallito.`,
          data: { productUpdateId: activity.id },
        });
      }
    }
  },
  ExportNotification: (queryClient, messageService, rawPayload) => {
    void queryClient.invalidateQueries({ queryKey: exportsQueryKeys.all() });
    const payload = rawPayload as ExportNotificationPayload;
    for (const exp of payload?.exports ?? []) {
      if (
        exp.status ===
          ExportNotificationPayloadExportsInnerStatusEnum.Completed &&
        payload.operation !== ExportNotificationPayloadOperationEnum.Deletion
      ) {
        messageService.add({
          severity: 'success',
          summary: $localize`Fatto`,
          detail: $localize`Esportazione completata.`,
        });
      } else if (exp.status === 'failed') {
        messageService.add({
          severity: 'error',
          summary: $localize`Errore`,
          detail: $localize`Esportazione fallita.`,
        });
      } else if (exp.status === 'canceled') {
        messageService.add({
          severity: 'warn',
          summary: $localize`Attenzione`,
          detail: $localize`Esportazione annullata.`,
        });
      }
    }
  },
  ImportNotification: (queryClient, messageService, rawPayload) => {
    const payload = rawPayload as ImportNotificationPayload;
    void queryClient.invalidateQueries({ queryKey: importsQueryKeys.all() });
    if (!importDialogOpen()) {
      for (const imp of payload?.imports ?? []) {
        if (notifiedImportIds.has(imp.id)) continue;
        if (
          imp.status ===
          ImportNotificationPayloadImportsInnerStatusEnum.Completed
        ) {
          messageService.add({
            severity: 'info',
            icon: 'pi-check-circle',
            summary: $localize`Importazione completata`,
            detail: $localize`L'importazione prodotti è stata completata.`,
            data: { importId: imp.id },
            closable: true,
          });
          notifiedImportIds.add(imp.id);
        } else if (
          imp.status === ImportNotificationPayloadImportsInnerStatusEnum.Failed
        ) {
          messageService.add({
            severity: 'error',
            icon: 'pi pi-info-circle',
            summary: $localize`Errore`,
            detail: $localize`L'importazione dei prodotti è fallita.`,
            data: { importId: imp.id },
          });
          notifiedImportIds.add(imp.id);
        }
      }
    }
  },
};

/** Route prefix → hub events to listen for on that area of the app. */
const ROUTE_REALTIME_EVENTS: readonly {
  match: (url: string) => boolean;
  events: readonly CompanyHubEventName[];
}[] = [
  {
    match: (url) => url.includes('/products/'),
    events: [
      'ProductNotification',
      'DigitalAssetNotification',
      'DigitalAssetTransformationNotification',
      'AiFieldContentGenerationNotification',
      'AiFieldTranslationNotification',
      'AiTextTranslationNotification',
    ],
  },
  {
    match: (url) => url.includes('/dam'),
    events: [
      'DigitalAssetNotification',
      'DigitalAssetTransformationNotification',
    ],
  },
  {
    match: (url) => url.includes('/gen-ai'),
    events: [
      'AiGenerationNotification',
      'DigitalAssetNotification',
      'ProductNotification',
    ],
  },
  {
    match: (url) => url.includes('/activities'),
    events: ['SyncNotification'],
  },
  {
    match: () => true,
    events: [
      'ImportNotification',
      'ExportNotification',
      'SyncNotification',
      'BulkUpdateProductActivityNotification',
    ],
  },
];

const DEBUG_PREFIX = '[CompanyRealtime]';
/** Flip to true to trace realtime hub lifecycle in the console (dev only). */
const REALTIME_DEBUG = false;
const debugLog = (...args: unknown[]): void => {
  if (REALTIME_DEBUG) {
    console.info(DEBUG_PREFIX, ...args);
  }
};

const eventsForUrl = (url: string): CompanyHubEventName[] => {
  const active = new Set<CompanyHubEventName>();
  for (const { match, events } of ROUTE_REALTIME_EVENTS) {
    if (match(url)) {
      for (const event of events) {
        active.add(event);
      }
    }
  }
  return [...active];
};

type CompanyNotificationPayload<T extends CompanyHubEventName> = Extract<
  CompanyHubSubscribe200Response,
  { eventName: T }
>['payload'];

@Injectable({ providedIn: 'root' })
export class CompanyRealtimeService {
  private readonly queryClient = inject(QueryClient);
  private readonly company = inject(COMPANY);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly navigationUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private hub: HubConnection | null = null;
  private companyId: string | null = null;
  private activeEvents: CompanyHubEventName[] = [];
  private offHandlers: (() => void) | null = null;
  /** Serializes connect/disconnect transitions so async runs never interleave. */
  private transitionQueue: Promise<void> = Promise.resolve();
  /** Active `waitForNotification` calls per hub, so a shared hub is only stopped by the last waiter. */
  private readonly hubWaiterCounts = new Map<HubConnection, number>();

  constructor() {
    effect(() => {
      const companyId = this.company();
      const url = this.navigationUrl();
      const events = eventsForUrl(url);
      debugLog('effect', { companyId, url, events });
      this.enqueueTransition(() => this.updateConnection(companyId, events));
    });

    this.destroyRef.onDestroy(() => {
      this.enqueueTransition(() => {
        this.activeEvents = [];
        return this.disconnectHub();
      });
    });
  }

  private enqueueTransition(transition: () => Promise<void>): void {
    this.transitionQueue = this.transitionQueue
      .then(transition)
      .catch((error) => {
        console.error('Company SignalR transition failed', error);
      });
  }

  async waitForNotification<T extends CompanyHubEventName>(
    companyId: string,
    eventName: T,
    options?: {
      check?: (payload: CompanyNotificationPayload<T>) => boolean;
      timeoutMs?: number;
    },
  ): Promise<CompanyNotificationPayload<T>> {
    const hub =
      this.getConnectedHub(companyId) ?? (await createSignalrHub(companyId));
    debugLog('waitForNotification', {
      companyId,
      eventName,
      hubState: hub.state,
    });
    this.hubWaiterCounts.set(hub, (this.hubWaiterCounts.get(hub) ?? 0) + 1);

    try {
      const payload = (await waitForFirstNotification(hub, eventName, {
        timeoutMs: options?.timeoutMs,
        check: options?.check as
          | ((payload: CompanyHubSubscribe200Response['payload']) => boolean)
          | undefined,
      })) as unknown as CompanyNotificationPayload<T>;
      debugLog('waitForNotification received', {
        eventName,
        payload,
      });
      return payload;
    } finally {
      const remaining = (this.hubWaiterCounts.get(hub) ?? 1) - 1;
      if (remaining > 0) {
        this.hubWaiterCounts.set(hub, remaining);
      } else {
        this.hubWaiterCounts.delete(hub);
        // Re-check now: `connect()` may have adopted this hub as the service
        // hub while we were waiting, and other waiters may share it.
        if (hub !== this.hub) {
          debugLog('waitForNotification stopping ephemeral hub');
          await hub.stop().catch(() => undefined);
        }
      }
    }
  }

  private getConnectedHub(companyId: string): HubConnection | null {
    if (
      this.companyId === companyId &&
      this.hub?.state === HubConnectionState.Connected
    ) {
      return this.hub;
    }
    return null;
  }

  private async updateConnection(
    companyId: string | null,
    events: CompanyHubEventName[],
  ): Promise<void> {
    const eventsKey = events.join('\0');
    const prevEventsKey = this.activeEvents.join('\0');
    const companyChanged = companyId !== this.companyId;
    const eventsChanged = eventsKey !== prevEventsKey;

    if (!companyId || events.length === 0) {
      debugLog('updateConnection: disconnect (no company or events)', {
        companyId,
        events,
      });
      this.activeEvents = [];
      await this.disconnectHub();
      return;
    }

    this.activeEvents = events;

    if (companyChanged) {
      debugLog('updateConnection: company changed, reconnecting', {
        from: this.companyId,
        to: companyId,
        events,
      });
      await this.disconnectHub();
      await this.connect(companyId);
      return;
    }

    if (!this.hub) {
      debugLog('updateConnection: no hub, connecting', {
        companyId,
        events,
      });
      await this.connect(companyId);
      return;
    }

    if (eventsChanged) {
      debugLog('updateConnection: events changed, refresh handlers', {
        prev: prevEventsKey.split('\0'),
        next: events,
      });
      this.refreshHandlers();
    }
  }

  private async connect(companyId: string): Promise<void> {
    if (this.activeEvents.length === 0) {
      return;
    }

    try {
      const hub = await createSignalrHub(companyId);
      if (this.company() !== companyId) {
        await hub.stop().catch(() => undefined);
        return;
      }

      this.hub = hub;
      this.companyId = companyId;
      this.refreshHandlers();
      debugLog('connected', {
        companyId,
        hubState: hub.state,
        events: this.activeEvents,
      });

      hub.onclose(() =>
        this.enqueueTransition(() => this.onHubClosed(companyId)),
      );
    } catch (error) {
      console.error('Company SignalR connection failed', error);
      await this.disconnectHub();
    }
  }

  private async onHubClosed(expectedCompanyId: string): Promise<void> {
    debugLog('hub closed', {
      expectedCompanyId,
      currentCompanyId: this.companyId,
    });
    if (this.companyId !== expectedCompanyId) {
      return;
    }

    await this.disconnectHub();

    const currentCompanyId = this.company();
    const url = this.navigationUrl();
    if (currentCompanyId) {
      await this.updateConnection(currentCompanyId, eventsForUrl(url));
    }
  }

  /** Stops the hub and removes handlers; keeps `activeEvents` for an immediate reconnect. */
  private async disconnectHub(): Promise<void> {
    debugLog('disconnect', {
      companyId: this.companyId,
      hubState: this.hub?.state,
    });
    this.offHandlers?.();
    this.offHandlers = null;

    const hub = this.hub;
    this.hub = null;
    this.companyId = null;

    if (hub) {
      await hub.stop().catch(() => undefined);
    }
  }

  private refreshHandlers(): void {
    this.offHandlers?.();
    this.offHandlers = null;

    if (!this.hub || this.activeEvents.length === 0) {
      debugLog('refreshHandlers: skipped', {
        hasHub: !!this.hub,
        activeEvents: this.activeEvents,
      });
      return;
    }

    debugLog('refreshHandlers', { events: this.activeEvents });
    this.offHandlers = this.attachHandlers(this.hub, this.activeEvents);
  }

  private attachHandlers(
    hub: HubConnection,
    events: readonly CompanyHubEventName[],
  ): () => void {
    const offs = events.map((eventName) => {
      const handler = (payload: unknown) => {
        debugLog('notification', { eventName, payload });
        void companyHubCacheInvalidators[eventName](
          this.queryClient,
          this.messageService,
          payload,
          this.router.url,
        );
      };
      hub.on(eventName, handler);
      return () => hub.off(eventName, handler);
    });

    return () => {
      for (const off of offs) {
        off();
      }
    };
  }
}
